import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type Profile } from "./api";
import { type Identity, saveIdentity } from "./identity";

const TOKEN_KEY = "playpal.token";

function readToken(): string | null {
	try {
		return localStorage.getItem(TOKEN_KEY);
	} catch {
		return null;
	}
}

function writeToken(token: string | null): void {
	try {
		if (token) localStorage.setItem(TOKEN_KEY, token);
		else localStorage.removeItem(TOKEN_KEY);
	} catch {
		// Playing as a guest still works without storage.
	}
}

export interface Session {
	/** `null` while playing as a guest. */
	profile: Profile | null;
	token: string | null;
	loading: boolean;
	signedIn: boolean;
	signIn: (input: { code: string; pin: string }) => Promise<void>;
	createProfile: (input: { name: string; avatar: string; pin: string }) => Promise<Profile>;
	signOut: () => Promise<void>;
	update: (changes: { name?: string; avatar?: string; globalOptIn?: boolean }) => Promise<void>;
	refresh: () => Promise<void>;
}

/**
 * A profile is optional throughout: everything plays without one, and signing
 * in only adds a record that follows you between devices.
 */
export function useSession(identity: Identity, onIdentity: (identity: Identity) => void): Session {
	const [token, setToken] = useState<string | null>(readToken);
	const [profile, setProfile] = useState<Profile | null>(null);
	const [loading, setLoading] = useState(token !== null);

	/** A signed-in player plays as their profile, not as the device's guest name. */
	const adopt = useCallback(
		(next: Profile) => {
			setProfile(next);
			const merged = { ...identity, name: next.name, avatar: next.avatar };
			saveIdentity(merged);
			onIdentity(merged);
		},
		[identity, onIdentity],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: adopt closes over the identity, so depending on it would refetch the profile on every rename.
	useEffect(() => {
		if (!token) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const me = await api.me(token);
				if (!cancelled) adopt(me.profile);
			} catch (error) {
				// An expired or revoked token just drops back to guest play.
				if (!cancelled && error instanceof ApiError && error.status === 401) {
					writeToken(null);
					setToken(null);
					setProfile(null);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// Deliberately only on token change: `adopt` closes over the identity,
		// and re-running on every keystroke of a name change is not wanted.
	}, [token]);

	const signIn = useCallback(
		async (input: { code: string; pin: string }) => {
			const result = await api.signIn(input);
			writeToken(result.token);
			setToken(result.token);
			adopt(result.profile);
		},
		[adopt],
	);

	const createProfile = useCallback(
		async (input: { name: string; avatar: string; pin: string }) => {
			const result = await api.createProfile(input);
			writeToken(result.token);
			setToken(result.token);
			adopt(result.profile);
			return result.profile;
		},
		[adopt],
	);

	const signOut = useCallback(async () => {
		const current = token;
		writeToken(null);
		setToken(null);
		setProfile(null);
		if (current) await api.signOut(current).catch(() => undefined);
	}, [token]);

	const update = useCallback(
		async (changes: { name?: string; avatar?: string; globalOptIn?: boolean }) => {
			if (!token) return;
			const result = await api.updateMe(token, changes);
			adopt(result.profile);
		},
		[token, adopt],
	);

	const refresh = useCallback(async () => {
		if (!token) return;
		const me = await api.me(token);
		adopt(me.profile);
	}, [token, adopt]);

	return {
		profile,
		token,
		loading,
		signedIn: profile !== null,
		signIn,
		createProfile,
		signOut,
		update,
		refresh,
	};
}
